import 'whatwg-fetch'
import 'unorm'
import 'array-flat-polyfill'
import Vue from 'vue'
import { AppShell } from './app-shell'
import createStore from './store'
// import './registerServiceWorker'
import i18n from './i18n'
import { checkIfExistsLanguage } from '@/utils/checkIfLanguageExists'

Vue.config.productionTip = false
Vue.config.ignoredElements = [/as-\w+/]

if (process.env.VUE_APP_VERSION) {
  // eslint-disable-next-line
  console.info(`SDK version: ${process.env.VUE_APP_VERSION}`)
}

// Disables transition for IE
var ua = window.navigator.userAgent
var msie = ua.indexOf('MSIE')
if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv:11\./)) {
  const span = {
    functional: true,
    render: (h, { data, children }) => h('span', data, children)
  }
  const div = {
    functional: true,
    render: (h, { data, children }) => children
  }
  Vue.component('transition', div)
  Vue.component('transition-group', span)
}
// End disables transition for IE

// Error handler
window.onerror = (message, source, lineno, colno, error) => {
  // message: error message (string). Available as event (sic!) in HTML onerror="" handler.
  // source: URL of the script where the error was raised (string)
  // lineno: Line number where error was raised (number)
  // colno: Column number for the line where the error occurred (number)
  // error: Error Object (object)
  onError({
    err: message,
    source,
    info: `in line ${lineno}:${colno}`
  })
}

Vue.config.errorHandler = (err, vm, info) => {
  // err: error trace
  // vm: component in which error occured
  // info: Vue specific error information such as lifecycle hooks, events etc.
  onError({
    err,
    source: vm,
    info
  })
}

const requireComponent = require.context(
  './components/common',
  true,
  /[A-Z]\w+\.(vue|js)$/
)

requireComponent.keys().forEach(fileName => {
  // Get component config
  const componentConfig = requireComponent(fileName)
  const componentName = fileName.split('/').pop().replace(/\.\w+$/, '')
  // Register component globally
  Vue.component(
    componentName,
    componentConfig.default || componentConfig
  )
})

export class AMap {
  constructor (id, {
    airport,
    building,
    floor,
    explorerMode,
    category,
    poi,
    poiD,
    route,
    language,
    printMode,
    lat,
    lon,
    mupiMode,
    avaMode,
    subcategories,
    filtering,
    zonaubicacion
  } = {}) {
    this.id = id
    this.airport = airport
    this.building = building
    this.floor = floor
    this.explorerMode = explorerMode
    this.category = category
    this.poi = poi
    this.poiD = poiD
    this.route = route
    this.language = language
    this.printMode = printMode
    this.lat = lat
    this.lon = lon
    this.mupiMode = mupiMode || avaMode
    this.avaMode = avaMode
    this.subcategories = subcategories
    this.filtering = filtering
    this.zonaubicacion = zonaubicacion
    try {
      this.checkErrors()
      this.render()
    } catch (error) {
      this.renderError(error)
      throw error
    }
  }

  setAirport (airport) {
    this.airport = airport

    this.render()
  }

  setBuilding (building) {
    this.building = building

    this.render()
  }

  setFloor (floor) {
    this.floor = floor

    this.render()
  }

  setPOI (poi) {
    this.poi = poi

    this.render()
  }

  setPoiCategory (floor, category) {
    this.floor = floor
    this.category = category

    this.render()
  }

  setLanguage (language) {
    this.language = language

    this.render()
  }

  setRoute (route) {
    this.route = route

    this.render()
  }

  setSubcategories (subcategories) {
    this.subcategories = subcategories

    this.render()
  }

  setFiltering (filtering) {
    this.filtering = filtering

    this.render()
  }

  checkErrors () {
    if (!hasValue(this.id)) {
      throw Error('Id is required')
    }
    if (!hasValue(this.airport)) {
      throw Error('Airport is required')
    }
    if (this.printMode && !this.explorerMode) {
      throw Error('Explorer mode are required with print mode is setted')
    }
    if (!this.explorerMode && !(hasValue(this.building) || hasValue(this.floor) || hasValue(this.route) || hasValue(this.poi))) {
      throw Error('Building or floor or poi ID are required when mode is in Context Mode')
    }
    if (hasValue(this.building) && !hasValue(this.floor)) {
      throw Error('Floor is required with building setted')
    }
    if (hasValue(this.category) && !(hasValue(this.building) || hasValue(this.floor))) {
      throw Error('Building and floor are required with category setted')
    }
    if (hasValue(this.subcategories) && !(hasValue(this.building) || hasValue(this.floor))) {
      if (!this.subcategories.length) {
        throw Error('Subcategories are required')
      }
      throw Error('Building and floor are required with subcategories setted')
    }
    if (hasValue(this.category) && hasValue(this.subcategories)) {
      throw Error('Category and subcategories cannot be used together')
    }
    if (hasValue(this.route) && !this.route.steps.length) {
      throw Error('Steps are required')
    }
    if (hasValue(this.mupiMode) && !hasValue(this.poi)) {
      throw Error('Poi ID is required with mupi mode')
    }
    if (hasValue(this.avaMode) && !hasValue(this.poi)) {
      throw Error('Poi ID is required with ava mode')
    }
  }

  render () {
    this.finalize()
    const language = this.language && checkIfExistsLanguage(this.language) ? this.language : process.env.VUE_APP_I18N_FALLBACK_LOCALE
    this.instance = new Vue({
      store: createStore(),
      i18n: i18n(language),
      data: {
        id: this.id,
        airport: this.airport,
        building: this.building,
        floor: this.floor,
        explorerMode: this.explorerMode,
        category: this.category,
        poi: this.poi,
        poiD: this.poiD,
        route: this.route,
        printMode: this.printMode,
        language,
        lat: this.lat,
        lon: this.lon,
        mupiMode: this.mupiMode,
        avaMode: this.avaMode,
        subcategories: this.subcategories,
        filtering: this.filtering,
        zonaubicacion: this.zonaubicacion
      },
      render: h => h(AppShell)
    }).$mount(this.id)
  }

  finalize () {
    if (!this.instance) {
      return
    }
    this.instance.$el.innerHTML = ''
    this.instance.$destroy()
  }

  renderError (error) {
    const el = document.getElementById(this.id.split('#')[1])

    const div = document.createElement('div')
    div.style.width = '100%'
    div.style.height = '100%'
    div.style.display = 'flex'
    div.style.justifyContent = 'center'
    div.style.alignItems = 'center'
    div.style.backgroundColor = '#9c9b9b'
    div.style.color = '#fff'
    div.innerText = error.toString()
    el.append(div)
  }
}

window.AMap = AMap

function onError ({ err, source, info }) {
  // eslint-disable-next-line
  console.error('Error: ', err)
  // eslint-disable-next-line
  console.error('Source: ', source)
  // eslint-disable-next-line
  console.error('Extra info: ', info)
}

function hasValue (value) {
  return value !== undefined && value !== null
}
